import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, Search, RefreshCw, Download, LogIn, Percent, Gift, CalendarPlus, ShieldAlert, Building2, ShieldX } from "lucide-react";

const ACTION_META = {
  impersonate:              { label: "Impersonate",       icon: LogIn,        tone: "text-blue-700 bg-blue-100 border-blue-400/25" },
  bulk_discount:            { label: "Bulk discount",     icon: Percent,      tone: "text-amber-700 bg-amber-100 border-amber-400/25" },
  trial_extension_approved: { label: "Trial extension ✓", icon: CalendarPlus, tone: "text-emerald-700 bg-emerald-100 border-emerald-400/25" },
  trial_extension_rejected: { label: "Trial extension ✗", icon: CalendarPlus, tone: "text-red-700 bg-red-100 border-red-400/25" },
  discount_approved:        { label: "Discount ✓",         icon: Percent,      tone: "text-emerald-700 bg-emerald-100 border-emerald-400/25" },
  discount_rejected:        { label: "Discount ✗",         icon: Percent,      tone: "text-red-700 bg-red-100 border-red-400/25" },
  complementary_approved:   { label: "Complementary ✓",   icon: Gift,          tone: "text-emerald-700 bg-emerald-100 border-emerald-400/25" },
  complementary_rejected:   { label: "Complementary ✗",   icon: Gift,          tone: "text-red-700 bg-red-100 border-red-400/25" },
  suspend:                  { label: "Suspend",           icon: ShieldAlert,   tone: "text-red-700 bg-red-100 border-red-400/25" },
  reactivate:               { label: "Reactivate",         icon: Activity,      tone: "text-emerald-700 bg-emerald-100 border-emerald-400/25" },
};

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const url = "/admin/audit-feed?limit=300" + (kind !== "all" ? `&kind=${kind}` : "");
    const r = await api.get(url);
    setRows(r.data.events || []);
    setLoading(false);
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = `${r.admin_email} ${r.entity_name || ""} ${r.action} ${JSON.stringify(r.meta || {})}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const header = ["When", "Admin", "Action", "Entity", "Meta"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push([
        (r.at || "").replace("T", " ").slice(0, 19),
        r.admin_email || "", r.action || "", r.entity_name || r.entity_id || "",
        JSON.stringify(r.meta || {}).replace(/"/g, '""'),
      ].map((v) => `"${v}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `zenith360-admin-audit-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="p-6 space-y-6 text-foreground" data-testid="admin-audit-page">
      <SessionKillSwitch />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Audit log</div>
          <h1 className="text-3xl font-bold mt-1">Admin action feed</h1>
          <div className="text-muted-foreground text-sm mt-1">Every impersonation, approval, discount and suspension — investor and partner ready.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/80" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search admin / entity…"
              className="h-9 rounded-sm bg-background text-foreground border-border pl-8 text-sm w-64"
              data-testid="audit-search" />
          </div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-9 w-48 rounded-sm bg-background text-foreground border-border" data-testid="audit-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Every action</SelectItem>
              <SelectItem value="impersonate">Impersonation</SelectItem>
              <SelectItem value="bulk_discount">Bulk discount</SelectItem>
              <SelectItem value="trial_extension_approved">Trial extensions ✓</SelectItem>
              <SelectItem value="discount_approved">Discounts ✓</SelectItem>
              <SelectItem value="complementary_approved">Complementary ✓</SelectItem>
              <SelectItem value="suspend">Suspensions</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-sm h-9 border-border bg-card hover:bg-muted text-foreground" data-testid="audit-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" onClick={exportCsv} className="rounded-sm h-9 border-border bg-card hover:bg-muted text-foreground gap-1.5" data-testid="audit-export">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <Card className="rounded-sm bg-card border-border p-10 text-center text-muted-foreground">
            <Activity className="w-10 h-10 mx-auto opacity-30" />
            <div className="mt-3">No events match.</div>
          </Card>
        )}
        {filtered.map((r) => {
          const meta = ACTION_META[r.action] || { label: r.action, icon: Activity, tone: "text-foreground/80 bg-card border-border" };
          const Icon = meta.icon;
          return (
            <Card key={r.id} className={`rounded-sm border p-3 flex items-start gap-3 ${meta.tone}`}
                    data-testid={`audit-row-${r.id.slice(0, 8)}`}>
              <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest">{meta.label}</span>
                  <span className="text-muted-foreground text-xs">by <b className="text-foreground">{r.admin_email}</b></span>
                </div>
                {r.entity_name && (
                  <div className="flex items-center gap-1.5 mt-1 text-sm">
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="font-medium">{r.entity_name}</span>
                  </div>
                )}
                {r.meta && Object.keys(r.meta).length > 0 && (
                  <div className="text-[11px] text-muted-foreground font-mono mt-1 break-all">
                    {Object.entries(r.meta).slice(0, 6).map(([k, v]) => (
                      <span key={k} className="mr-3">
                        <span className="text-muted-foreground/80">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground/80 whitespace-nowrap">
                {r.at ? new Date(r.at).toLocaleString() : "—"}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// Iter-186 · Session Kill-Switch — Super-Admin "Revoke All Sessions"
// Nukes every JWT platform-wide (or scoped to one org id) in a single click.
// ═════════════════════════════════════════════════════════════════════════════
function SessionKillSwitch() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [orgId, setOrgId] = useState("");
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/sessions/status"); setStatus(r.data); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (confirm !== "REVOKE") return toast.error('Type REVOKE to confirm');
    setBusy(true);
    try {
      const body = {};
      if (orgId.trim()) body.org_id = orgId.trim();
      if (reason.trim()) body.reason = reason.trim();
      const r = await api.post("/admin/sessions/revoke-all", body);
      toast.success(
        `Revoked · ${r.data.scope === 'platform' ? 'ALL sessions across every tenant' : `Sessions for org ${body.org_id}`}. Every JWT is now invalidated.`,
        { duration: 8000 });
      setConfirm(""); setReason(""); setOrgId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  const platformEpoch = status?.epochs?.find(e => e.key === 'sessions_epoch');
  return (
    <Card className="rounded-sm border-red-500/40 bg-red-500/5 p-5" data-testid="session-kill-switch">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-sm bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
          <ShieldX className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-600">// Kill-switch</div>
          <h2 className="text-xl font-bold mt-0.5">Revoke every session</h2>
          <p className="text-sm text-foreground/80 mt-1 leading-relaxed">
            Invalidates every JWT — every browser tab, mobile app and script — everywhere.
            Users are signed out immediately and must re-authenticate. Use if a leak or
            stolen token is suspected. <span className="text-muted-foreground">Currently active sessions in DB: <b className="text-foreground">{status?.active_sessions ?? "…"}</b>.</span>
          </p>
          {platformEpoch?.value && (
            <div className="text-[11px] font-mono text-red-300/80 mt-2">
              Last platform-wide revoke: {new Date(platformEpoch.value * 1000).toLocaleString()} · by {platformEpoch.updated_by}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-2 mt-4">
            <Input placeholder="Scope: org_id (leave blank = ALL)" value={orgId} onChange={(e) => setOrgId(e.target.value)}
              className="rounded-sm bg-background border-border text-foreground h-9 text-xs" data-testid="revoke-org-id" />
            <Input placeholder="Reason (audit log)" value={reason} onChange={(e) => setReason(e.target.value)}
              className="rounded-sm bg-background border-border text-foreground h-9 text-xs" data-testid="revoke-reason" />
            <Input placeholder='Type REVOKE to confirm' value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="rounded-sm bg-background border-red-500/40 text-foreground h-9 text-xs font-mono" data-testid="revoke-confirm" />
          </div>
          <div className="mt-3">
            <Button onClick={revoke} disabled={busy || confirm !== 'REVOKE'} data-testid="revoke-btn"
              className="rounded-sm bg-red-600 hover:bg-red-500 text-foreground h-9 disabled:opacity-40 gap-1.5">
              <ShieldX className="w-3.5 h-3.5" /> {busy ? "Revoking…" : (orgId.trim() ? "Revoke sessions for this org" : "Revoke ALL platform sessions")}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
